import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminRoleService } from '@/shared/api'
import type { AdminRoleWriteRequest } from '@/shared/api'

const KEYS = {
  roles: ['admin-roles'] as const,
  catalog: ['admin-roles', 'catalog'] as const,
}

export function useAdminRoles() {
  return useQuery({
    queryKey: KEYS.roles,
    queryFn: () => AdminRoleService.getRoles(),
    staleTime: 60 * 1000,
  })
}

export function useAdminRoleCatalog() {
  return useQuery({
    queryKey: KEYS.catalog,
    queryFn: () => AdminRoleService.getCatalog(),
    staleTime: 30 * 60 * 1000,
  })
}

function useRoleMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>, successMessage: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(successMessage)
      queryClient.invalidateQueries({ queryKey: KEYS.roles })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) toast.error(error.detail || 'Ошибка операции')
    },
  })
}

export function useCreateAdminRole() {
  return useRoleMutation(
    (request: AdminRoleWriteRequest) => AdminRoleService.createRole(request),
    'Роль создана')
}

export function useUpdateAdminRole() {
  return useRoleMutation(
    ({ id, request }: { id: number; request: AdminRoleWriteRequest }) =>
      AdminRoleService.updateRole(id, request),
    'Роль сохранена')
}

export function useDeleteAdminRole() {
  return useRoleMutation(
    (id: number) => AdminRoleService.deleteRole(id),
    'Роль удалена')
}

export function useAssignAdminRole() {
  return useRoleMutation(
    ({ userId, adminRoleId }: { userId: number; adminRoleId: number | null }) =>
      AdminRoleService.assignRole(userId, adminRoleId),
    'Роль сотрудника обновлена')
}
